import React from 'react'
import propTypes from 'prop-types'
import moment from 'moment'
import { Card, CardContent, Chip, IconButton, Tooltip, Typography, makeStyles } from '@material-ui/core'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import { getAssetSubtitle, getTypeIcon } from '../resources/ContentAssetTypeIcons'

const useStyles = makeStyles((theme) => ({
	card: {
		width: '100%',
		minHeight: 220,
		display: 'flex',
		flexDirection: 'column',
		position: 'relative'
	},
	kebab: {
		position: 'absolute',
		top: theme.spacing(0.5),
		right: theme.spacing(0.5)
	},
	iconWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: 80,
		backgroundColor: theme.palette.primary.light,
		color: theme.palette.secondary.contrastText,
		borderBottom: `1px solid ${theme.palette.divider}`
	},
	icon: {
		fontSize: 56
	},
	content: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(0.5)
	},
	title: {
		fontWeight: 600,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		display: '-webkit-box',
		WebkitLineClamp: 2,
		WebkitBoxOrient: 'vertical'
	},
	subtitle: {
		color: theme.palette.text.secondary
	},
	category: {
		color: theme.palette.text.secondary
	},
	tagsRow: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(0.5),
		marginTop: 'auto'
	},
	tagChip: {
		height: 20
	},
	created: {
		color: theme.palette.text.secondary,
		marginTop: theme.spacing(0.5)
	}
}))

const MAX_VISIBLE_TAGS = 3
const MAX_VISIBLE_AUDIENCES = 2

const AUDIENCE_LABELS = {
	ALL: 'All',
	SERVICE_PROVIDER: 'Service provider',
	CUSTOMER: 'Customer',
	SALES_REPRESENTATIVE: 'Sales rep',
	IMBERA: 'Imbera',
	RETAILER: 'Retailer'
}

const formatAudienceLabel = (code) => AUDIENCE_LABELS[code] || code

/**
 * Grid-mode card for a ContentAsset list item. Mirrors the data shown in the
 * list-mode row, minus the AUDIENCE column (no card-level analog in this slice).
 */
const ContentAssetCard = (props) => {
	const classes = useStyles()
	const { item, onClick, onKebabClick, showAudience } = props
	const TypeIcon = getTypeIcon(item.Type)
	const tags = Array.isArray(item.Tags) ? item.Tags : []
	const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS)
	const overflow = tags.length - visibleTags.length

	const cardProps = onClick
		? { onClick, style: { cursor: 'pointer' } }
		: {}

	return (
		<Card className={classes.card} {...cardProps}>
			{onKebabClick && (
				<Tooltip title='Actions'>
					<span onClick={(e) => e.stopPropagation()}>
						<IconButton
							size='small'
							className={classes.kebab}
							aria-label='actions'
							onClick={(e) => onKebabClick(e, item)}
						>
							<MoreVertIcon fontSize='small' />
						</IconButton>
					</span>
				</Tooltip>
			)}
			<div className={classes.iconWrapper}>
				<TypeIcon className={classes.icon} />
			</div>
			<CardContent className={classes.content}>
				<Typography variant='subtitle2' className={classes.title} title={item.Title}>
					{item.Title}
				</Typography>
				<Typography variant='caption' className={classes.subtitle} noWrap>
					{getAssetSubtitle(item)}
				</Typography>
				<Typography variant='caption' className={classes.category} noWrap>
					{item.CategoryName || ''}
				</Typography>
				<div className={classes.tagsRow}>
					{visibleTags.length === 0 ? (
						<Typography variant='caption' className={classes.subtitle}>
							No tags
						</Typography>
					) : (
						<>
							{visibleTags.map((tag) => (
								<Chip key={tag.Id} size='small' label={tag.Name} className={classes.tagChip} />
							))}
							{overflow > 0 && <Chip size='small' label={`+${overflow}`} className={classes.tagChip} />}
						</>
					)}
				</div>
				{showAudience && Array.isArray(item.AudienceCodes) && item.AudienceCodes.length > 0 && (
					<div className={classes.tagsRow}>
						{item.AudienceCodes.slice(0, MAX_VISIBLE_AUDIENCES).map((code) => (
							<Chip
								key={code}
								size='small'
								variant='outlined'
								label={formatAudienceLabel(code)}
								className={classes.tagChip}
							/>
						))}
						{item.AudienceCodes.length > MAX_VISIBLE_AUDIENCES && (
							<Chip
								size='small'
								variant='outlined'
								label={`+${item.AudienceCodes.length - MAX_VISIBLE_AUDIENCES}`}
								className={classes.tagChip}
							/>
						)}
					</div>
				)}
				<Typography variant='caption' className={classes.created}>
					{item.CreationDate ? moment(item.CreationDate).fromNow() : ''}
				</Typography>
			</CardContent>
		</Card>
	)
}

ContentAssetCard.propTypes = {
	item: propTypes.shape({
		Id: propTypes.number,
		Token: propTypes.string,
		Type: propTypes.number,
		Title: propTypes.string,
		CategoryName: propTypes.string,
		ExternalUrl: propTypes.string,
		OriginalFilename: propTypes.string,
		Tags: propTypes.array,
		CreationDate: propTypes.string
	}).isRequired,
	onClick: propTypes.func,
	onKebabClick: propTypes.func,
	showAudience: propTypes.bool
}

ContentAssetCard.defaultProps = {
	showAudience: false
}

export default ContentAssetCard
